import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  LocalShippingOutlined,
  MopedOutlined,
  PendingActionsOutlined,
  RefreshOutlined,
  FlashOnOutlined,
  CancelOutlined,
  CheckCircleOutline,
  AccessTimeOutlined,
  StorefrontOutlined,
  WarningAmberOutlined,
  TimelineOutlined,
  AssignmentTurnedInOutlined,
  SyncProblemOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import { ordersService, couriersService, dashboardService } from '../api/services';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const { t, isRtl, dir } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    newOrders: 0,
    searchingCourierOrders: 0,
    assignedOrders: 0,
    atRestaurantOrders: 0,
    outForDeliveryOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    returnedOrders: 0,
    staleOrders: 0,
    availableCouriers: 0,
    busyCouriers: 0,
    activeRestaurants: 0,
    deliverySuccessRate: 0,
    averageDeliveryMinutes: 0,
  });
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [dispatchStrategy, setDispatchStrategy] = useState('Parallel');
  const [dispatchTimeout, setDispatchTimeout] = useState('45');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [liveOrdersRes, liveCouriersRes, summaryRes, exceptionsRes] = await Promise.allSettled([
        ordersService.getOrders({ status: 'active', page: 1, pageSize: 25 }),
        couriersService.getCouriers({ isOnline: true }),
        dashboardService.getSummary(),
        dashboardService.getExceptions(null, null, 15),
      ]);

      if (liveOrdersRes.status === 'fulfilled' && liveOrdersRes.value) {
        const oVal = liveOrdersRes.value;
        setOrders(Array.isArray(oVal) ? oVal : (oVal.items || []));
      } else {
        setOrders([]);
      }

      if (liveCouriersRes.status === 'fulfilled' && liveCouriersRes.value) {
        const cVal = liveCouriersRes.value;
        setCouriers(Array.isArray(cVal) ? cVal : (cVal.items || []));
      } else {
        setCouriers([]);
      }

      if (summaryRes.status === 'fulfilled' && summaryRes.value) {
        setSummary(summaryRes.value);
      }

      if (exceptionsRes.status === 'fulfilled' && exceptionsRes.value) {
        setExceptions(Array.isArray(exceptionsRes.value) ? exceptionsRes.value : (exceptionsRes.value.items || []));
      }
    } catch (e) {
      console.warn('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleOpenDispatch = (order) => {
    setSelectedOrder(order);
    setDispatchModalOpen(true);
  };

  const handleExecuteDispatch = async () => {
    if (!selectedOrder) return;
    try {
      const oId = selectedOrder.id || selectedOrder.orderId;
      await ordersService.startParallelDispatch(oId, {
        strategy: dispatchStrategy,
        offerTimeoutSeconds: parseInt(dispatchTimeout, 10),
      });
      setToast({
        open: true,
        message: `${isRtl ? 'تم إطلاق الإسناد المتوازي للطلب' : 'Parallel dispatch launched for order'} #${oId}`,
        severity: 'success',
      });
      fetchDashboardData();
    } catch (e) {
      setToast({
        open: true,
        message: `Dispatch error: ${e.message || ''}`,
        severity: 'error',
      });
    } finally {
      setDispatchModalOpen(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await ordersService.cancelOrder(orderId, 'Dashboard manual cancel', 'PreDispatch');
      setToast({ open: true, message: `${isRtl ? 'تم إلغاء الطلب' : 'Cancelled order'} #${orderId}`, severity: 'info' });
      fetchDashboardData();
    } catch (e) {
      setToast({ open: true, message: `Failed: ${e.message || ''}`, severity: 'error' });
    }
  };

  // Pipeline Statuses derived from summary response
  const activeOrdersCount = (summary.newOrders || 0) +
    (summary.searchingCourierOrders || 0) +
    (summary.assignedOrders || 0) +
    (summary.atRestaurantOrders || 0) +
    (summary.outForDeliveryOrders || 0);

  const pipelineStages = [
    { key: 'new', label: isRtl ? 'طلبات جديدة' : 'New', count: summary.newOrders || 0, color: '#3B82F6', icon: PendingActionsOutlined },
    { key: 'searching', label: isRtl ? 'بحث عن مندوب' : 'Searching Courier', count: summary.searchingCourierOrders || 0, color: '#F59E0B', icon: FlashOnOutlined },
    { key: 'assigned', label: isRtl ? 'تم الإسناد' : 'Assigned', count: summary.assignedOrders || 0, color: '#8B5CF6', icon: AssignmentTurnedInOutlined },
    { key: 'atRestaurant', label: isRtl ? 'بالمطعم للاستلام' : 'At Restaurant', count: summary.atRestaurantOrders || 0, color: '#06B6D4', icon: StorefrontOutlined },
    { key: 'outForDelivery', label: isRtl ? 'جاري التوصيل' : 'Out for Delivery', count: summary.outForDeliveryOrders || 0, color: '#EF6C00', icon: LocalShippingOutlined },
    { key: 'delivered', label: isRtl ? 'تم التسليم' : 'Delivered', count: summary.deliveredOrders || 0, color: '#10B981', icon: CheckCircleOutline },
    { key: 'cancelled', label: isRtl ? 'ملغي / مرتجع' : 'Cancelled/Returned', count: (summary.cancelledOrders || 0) + (summary.returnedOrders || 0), color: '#EF4444', icon: CancelOutlined },
  ];

  return (
    <Layout>
      {/* Top Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('home.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('home.subtitle')} • {isRtl ? 'تحديث تشغيلي فوري' : 'Live operational stream'}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshOutlined sx={{ fontSize: 16 }} />}
          onClick={fetchDashboardData}
          disabled={loading}
          sx={{ borderRadius: '8px', fontSize: '0.78rem', py: 0.6 }}
        >
          {loading ? t('loading') : t('refresh')}
        </Button>
      </Box>

      {/* Top 4 KPI Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('home.totalOrdersToday')}
            value={((summary.totalOrders !== undefined && summary.totalOrders !== null) ? summary.totalOrders : orders.length).toString()}
            icon={LocalShippingOutlined}
            color="#EF6C00"
            subtitle={`${summary.deliveredOrders || 0} ${isRtl ? 'تم تسليمها' : 'delivered'} (${parseFloat(summary.deliverySuccessRate || 0).toFixed(1)}% ${isRtl ? 'نجاح' : 'success'})`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'الطلبات النشطة حالياً' : 'Active Orders Now'}
            value={activeOrdersCount.toString()}
            icon={PendingActionsOutlined}
            color="#F59E0B"
            subtitle={`${summary.searchingCourierOrders || 0} ${isRtl ? 'قيد البحث' : 'searching'} • ${summary.outForDeliveryOrders || 0} ${isRtl ? 'بالطريق' : 'on way'}`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'أسطول المناديب' : 'Fleet Status'}
            value={((summary.availableCouriers || 0) + (summary.busyCouriers || 0) || couriers.length).toString()}
            icon={MopedOutlined}
            color="#10B981"
            subtitle={`${summary.availableCouriers || 0} ${isRtl ? 'متاح الآن' : 'available'} • ${summary.busyCouriers || 0} ${isRtl ? 'في مهمة' : 'busy'}`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'المطاعم ومتوسط التوصيل' : 'Partners & Delivery Time'}
            value={`${summary.activeRestaurants || 0} ${isRtl ? 'مطعم نشط' : 'Active'}`}
            icon={StorefrontOutlined}
            color="#3B82F6"
            subtitle={`${parseFloat(summary.averageDeliveryMinutes || 0).toFixed(1)} ${isRtl ? 'دقيقة متوسط التوصيل' : 'min avg delivery'}`}
          />
        </Grid>
      </Grid>

      {/* Operational Order Flow Pipeline Strip */}
      <Card sx={{ mb: 2.5, borderRadius: '12px', overflow: 'hidden' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineOutlined sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {isRtl ? 'دورة حياة وتدفق الطلبات التشغيلية (Order Pipeline)' : 'Operational Order Flow Pipeline'}
              </Typography>
            </Box>

            {summary.staleOrders > 0 && (
              <Chip
                icon={<WarningAmberOutlined sx={{ fontSize: 14 }} />}
                label={`${summary.staleOrders} ${isRtl ? 'طلبات متأخرة' : 'Stale Orders'}`}
                color="error"
                size="small"
                sx={{ fontWeight: 800, fontSize: '0.72rem' }}
              />
            )}
          </Box>

          <Grid container spacing={1.2}>
            {pipelineStages.map((stage) => {
              const Icon = stage.icon;
              return (
                <Grid item xs={6} sm={4} md={12 / 7} key={stage.key}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '10px',
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 78,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                        borderColor: stage.color,
                      },
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    {/* Stage Label & Icon Centered */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.6,
                        mb: 0.8,
                        width: '100%',
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      <Icon sx={{ fontSize: 16, color: stage.color }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: 'text.secondary',
                          fontSize: '0.74rem',
                        }}
                        style={{
                          textAlign: 'center',
                          margin: '0',
                        }}
                      >
                        {stage.label}
                      </Typography>
                    </Box>

                    {/* Number Centered in Badge */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 36,
                          height: 28,
                          borderRadius: '14px',
                          bgcolor: `${stage.color}14`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 1.2,
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 800,
                            color: stage.color,
                            fontSize: '1rem',
                            lineHeight: 1,
                          }}
                          style={{
                            textAlign: 'center',
                            display: 'block',
                            margin: '0 auto',
                          }}
                        >
                          {stage.count}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Workspace Grid */}
      <Grid container spacing={2}>
        {/* Main Orders Feed */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%', borderRadius: '12px' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlashOnOutlined sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('home.liveOrdersFeed')}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {orders.length} {isRtl ? 'طلبات مسجلة' : 'registered orders'}
                </Typography>
              </Box>

              {loading ? (
                <LoadingState />
              ) : orders.length === 0 ? (
                <EmptyState message={t('noData')} subtitle={isRtl ? 'سيتم عرض الطلبات فور إدخالها من بوابة المطاعم' : 'Orders will appear here as soon as they are placed'} />
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 680 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 110 }}>{t('ordersPage.orderNumber')}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 160 }}>{t('ordersPage.restaurant')} / {t('ordersPage.customer')}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 160 }}>{t('address')}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{t('ordersPage.orderTotal')}</TableCell>
                        <TableCell sx={{ textAlign: 'center !important', width: 120 }}>{t('status')}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{t('ordersPage.courier')}</TableCell>
                        <TableCell sx={{ textAlign: 'center !important', width: 90 }}>{t('actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.map((order) => {
                        const oId = order.id || order.orderId;
                        return (
                          <TableRow key={oId} hover>
                            <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.8rem' }}>
                                #{oId}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: 'text.secondary', fontSize: '0.7rem' }}>
                                <AccessTimeOutlined sx={{ fontSize: 11 }} />
                                <span>{order.createdAt || (isRtl ? 'الآن' : 'Now')}</span>
                              </Box>
                            </TableCell>

                            <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                {order.restaurant || order.restaurantName || (isRtl ? 'مطعم' : 'Restaurant')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                {order.client || order.customerName || (isRtl ? 'عميل' : 'Customer')}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {order.address || order.customerAddressText || order.deliveryAddress || (isRtl ? 'العنوان' : 'Address')}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem' }}>
                                {order.totalAmount || order.customerCollectionAmount || '0.00'} {t('currency')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                                ({isRtl ? 'توصيل' : 'Fee'}: {order.deliveryFee || 0})
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ textAlign: 'center !important' }}>
                              <StatusBadge status={order.status} size="small" />
                            </TableCell>

                            <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {order.courierName || (isRtl ? 'غير مسند' : 'Unassigned')}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ textAlign: 'center !important', whiteSpace: 'nowrap' }}>
                              {(order.status === 'PendingDispatch' || order.status === 'Draft' || !order.courierId) && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<FlashOnOutlined sx={{ fontSize: 14 }} />}
                                  onClick={() => handleOpenDispatch(order)}
                                  sx={{
                                    fontSize: '0.72rem',
                                    py: 0.4,
                                    px: 1,
                                    minWidth: 75,
                                  }}
                                >
                                  {t('home.assignCourier')}
                                </Button>
                              )}

                              {order.status !== 'Delivered' && order.status !== 'Cancelled' && (
                                <Tooltip title={isRtl ? 'إلغاء الطلب' : 'Cancel Order'}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleCancelOrder(oId)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <CancelOutlined sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Sidebar: Operational Exceptions & Fleet Status */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Operational Exceptions Card */}
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <SyncProblemOutlined sx={{ color: 'warning.main', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {isRtl ? 'استثناءات التشغيل الحية' : 'Operational Exceptions'}
                    </Typography>
                  </Box>
                  <Chip label={exceptions.length} size="small" color={exceptions.length > 0 ? 'warning' : 'default'} sx={{ fontWeight: 800 }} />
                </Box>

                {exceptions.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                    {isRtl ? 'لا توجد استثناءات تشغيلية، المنظومة تعمل بانتظام' : 'No active operational exceptions'}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {exceptions.slice(0, 5).map((ex, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.2,
                          borderRadius: '8px',
                          bgcolor: 'action.hover',
                          borderLeft: '4px solid',
                          borderColor: ex.severity >= 3 ? 'error.main' : 'warning.main',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
                            {ex.orderNumber || `#${ex.orderId}`}
                          </Typography>
                          <Chip label={ex.code || 'EXCEPTION'} size="small" color={ex.severity >= 3 ? 'error' : 'warning'} variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.72rem' }}>
                          {ex.description || 'Order requires operator attention'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Couriers On Duty Card */}
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <MopedOutlined sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t('home.courierFleetStatus')}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {couriers.length} {isRtl ? 'كابتن' : 'couriers'}
                  </Typography>
                </Box>

                {loading ? (
                  <LoadingState message={t('loading')} />
                ) : couriers.length === 0 ? (
                  <EmptyState message={t('noData')} subtitle={isRtl ? 'عندما يبدأ الكباتن وردياتهم سيظهرون هنا مباشرة' : 'Couriers will appear here once online'} />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {couriers.slice(0, 6).map((courier) => (
                      <Box
                        key={courier.id || courier.courierId}
                        sx={{
                          p: 1,
                          borderRadius: '8px',
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: courier.status === 'Online' || courier.status === 1 ? 'success.light' : 'warning.light',
                              color: courier.status === 'Online' || courier.status === 1 ? 'success.dark' : 'warning.dark',
                              fontWeight: 700,
                              width: 30,
                              height: 30,
                              fontSize: '0.75rem',
                            }}
                          >
                            {(courier.fullNameAr || courier.name || 'C').charAt(0)}
                          </Avatar>
                          <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.2 }}>
                              {courier.fullNameAr || courier.name || (isRtl ? 'كابتن' : 'Courier')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                              {courier.mobile || courier.phone || ''}
                            </Typography>
                          </Box>
                        </Box>

                        <StatusBadge status={courier.status || 'Online'} size="small" />
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Parallel Dispatch Dialog */}
      <Dialog
        open={dispatchModalOpen}
        onClose={() => setDispatchModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? 'إطلاق الإسناد المتوازي (Parallel Dispatch)' : 'Launch Parallel Dispatch'}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ my: 1 }}>
              <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', mb: 2, textAlign: isRtl ? 'right' : 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  #{selectedOrder.id || selectedOrder.orderId} • {selectedOrder.restaurant || selectedOrder.restaurantName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {selectedOrder.address || selectedOrder.deliveryAddress} • {t('ordersPage.orderTotal')}: {selectedOrder.totalAmount || selectedOrder.customerCollectionAmount} {t('currency')}
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', textAlign: isRtl ? 'right' : 'left' }}>
                {isRtl ? 'استراتيجية الإسناد' : 'Dispatch Strategy'}
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={dispatchStrategy}
                onChange={(e) => setDispatchStrategy(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="Parallel">{isRtl ? 'إسناد متوازي لكافة الكباتن المتاحين' : 'Broadcast to all online couriers'}</MenuItem>
                <MenuItem value="ClosestFirst">{isRtl ? 'الأقرب جغرافياً أولاً' : 'Closest courier first'}</MenuItem>
              </TextField>

              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', textAlign: isRtl ? 'right' : 'left' }}>
                {isRtl ? 'مهلة العرض للكابتن (بالثواني)' : 'Offer Timeout (Seconds)'}
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={dispatchTimeout}
                onChange={(e) => setDispatchTimeout(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleExecuteDispatch}
            startIcon={<FlashOnOutlined />}
            sx={{ px: 2, fontWeight: 700 }}
          >
            {isRtl ? 'إطلاق العروض' : 'Broadcast Offer'}
          </Button>
          <Button onClick={() => setDispatchModalOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Home;
